import { useCallback, useEffect, useRef, useState } from 'react'

import { type AIFieldConfig, getGenerateFieldOptions } from '@/configs/aiFieldConfigs'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { buildFAQListString, extractFAQContext } from '@/utils/faqHelpers'

import {
  getForm,
  getIdParams,
  logAIGenerationError,
  normalizeAISuggestion,
  type PollEntityType,
  pollForGenerationResult,
} from './services/aiGenerationService'
import type { AICompatibleForm, AIFieldState, AIGenerationContext, FormType } from './types/aiFieldGeneration'

const OVERLAPPING_FIELDS = ['name', 'shortDescription', 'faqs', 'faqSingle'] as const

function getStateKey(fieldName: string, formType?: FormType): string {
  if (formType === 'cm' || formType === 'step') {
    if (OVERLAPPING_FIELDS.includes(fieldName as (typeof OVERLAPPING_FIELDS)[number])) {
      return `${formType}_${fieldName}`
    }
  }
  return fieldName
}

function setFormFieldValue(form: AICompatibleForm, field: string, value: string): void {
  form.setFieldValue(field, value)
}

export function useAIFieldGeneration(props: CMFormSection) {
  const { api } = useGlobalState()

  // Ref so callbacks read latest props without needing them in dependency arrays.
  const propsRef = useRef(props)
  propsRef.current = props

  const [fieldStates, setFieldStates] = useState<Record<string, AIFieldState>>({})
  const activeRequestsRef = useRef<Set<string>>(new Set())
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
  const titleSeedingDoneRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      activeRequestsRef.current.clear()
      abortControllersRef.current.forEach(c => c.abort())
      abortControllersRef.current.clear()
      setFieldStates({})
    }
  }, [])

  const getFieldState = useCallback(
    (fieldName: string, formType?: FormType): AIFieldState => {
      const key = getStateKey(fieldName, formType)
      return fieldStates[key] || { state: 'idle', suggestion: null }
    },
    [fieldStates],
  )

  const updateFieldState = useCallback((fieldName: string, updates: Partial<AIFieldState>, formType?: FormType) => {
    const key = getStateKey(fieldName, formType)
    setFieldStates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { state: 'idle', suggestion: null }), ...updates },
    }))
  }, [])

  // Seed step title suggestion from task_steps titleAi when name is blank (no extra API call)
  // Only update step-scoped state so the main CM form title never shows this suggestion.
  // Guarded by titleSeedingDoneRef to ensure seeding is evaluated at most once per step.
  useEffect(() => {
    if (!props.stepId || !props.stepsQuery?.data?.length) return
    if (titleSeedingDoneRef.current === props.stepId) return

    const step = props.stepsQuery.data.find(s => s.stepId === props.stepId)
    if (!step?.titleAi?.trim()) return

    titleSeedingDoneRef.current = props.stepId

    const name = props.stepForm.getValues().name
    if (name?.trim()) return

    const nameState = getFieldState('name', 'step')
    if (nameState.state !== 'idle' || nameState.suggestion != null) return

    updateFieldState('name', { state: 'pending', suggestion: step.titleAi.trim(), previousValue: name ?? '' }, 'step')
  }, [props.stepId, props.stepsQuery?.data, props.stepForm, getFieldState, updateFieldState])

  // Unified field generation — reads props via propsRef to keep deps stable.
  const generateFieldUnified = useCallback(
    async (config: AIFieldConfig & { formType: FormType }, context?: AIGenerationContext) => {
      const currentProps = propsRef.current
      const { formField, formType } = config
      const stateKey = getStateKey(formField, formType)

      // Check if request is already in progress
      if (activeRequestsRef.current.has(stateKey)) {
        return
      }

      // Track this request and create an AbortController for cancellation
      activeRequestsRef.current.add(stateKey)
      const abortController = new AbortController()
      abortControllersRef.current.set(stateKey, abortController)

      // Capture current value so we can revert on reject (uncontrolled mode).
      const form = getForm(currentProps, formType) as AICompatibleForm | undefined
      const previousValue = (form?.getValues()?.[formField] as string) ?? ''

      updateFieldState(formField, { state: 'loading', previousValue }, formType)

      try {
        const idParams = getIdParams(formType, currentProps)

        if (config.type === 'metadata') {
          const form = getForm(currentProps, formType) as AICompatibleForm | undefined
          const currentValues = form?.getValues?.()
          const acceptedDataRaw = currentValues ? (currentValues[formField] as unknown) : undefined
          const acceptedDataValue = typeof acceptedDataRaw === 'string' ? acceptedDataRaw : undefined

          // Get current field state to check for pending suggestions
          const currentFieldState = getFieldState(formField, formType)
          const proposedDataValue = currentFieldState.state === 'pending' ? currentFieldState.suggestion : undefined

          const response = await api.safeRequest(api.generateMetadata, {
            ...idParams,
            number_of_options: 1,
            field: config.apiParamField,
            extra_data: {
              field_name: config.apiParamField,
              accepted_data: acceptedDataValue || undefined,
              proposed_data: proposedDataValue || undefined,
            },
          })

          if (abortController.signal.aborted) return

          if (response.ok && response.data.generated?.[0]?.[config.apiResponseField]) {
            const suggestion = response.data.generated[0][config.apiResponseField] as string
            updateFieldState(formField, { state: 'pending', suggestion }, formType)
          } else {
            logAIGenerationError('Metadata API response invalid or empty', response, formField)
            updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
          }
        } else if (config.type === 'generate') {
          if (formType !== 'cm' && formType !== 'step') {
            throw new Error('Generate endpoint only supports content modules and steps')
          }

          const form = getForm(currentProps, formType) as AICompatibleForm | undefined
          const currentValues = form?.getValues?.()
          const acceptedDataRaw = currentValues ? (currentValues[formField] as unknown) : undefined
          const acceptedDataValue = typeof acceptedDataRaw === 'string' ? acceptedDataRaw : undefined

          // Get current field state to check for pending suggestions
          const currentFieldState = getFieldState(formField, formType)
          const proposedDataValue = currentFieldState.state === 'pending' ? currentFieldState.suggestion : undefined

          // Construct the appropriate ID parameter based on form type
          const idParams = formType === 'cm' ? { task_id: currentProps.taskId! } : { step_id: currentProps.stepId! }

          // Build extra_data object
          const extraData: Record<string, string | undefined> = {}

          // Handle per-FAQ generation (uses FAQ-specific context instead of standard fields)
          if (formField === 'faqSingle' && context?.faqId && context?.faqList) {
            const targetFAQ = context.faqList.find(faq => faq.id === context.faqId)

            if (targetFAQ) {
              // Extract FAQ-specific context (current question/answer and pending suggestion)
              const faqContext = extractFAQContext(targetFAQ)
              if (faqContext.acceptedData) extraData.accepted_data = faqContext.acceptedData
              if (faqContext.proposedData) extraData.proposed_data = faqContext.proposedData

              // Build FAQ list for context (all other FAQs to avoid duplicates)
              const faqList = buildFAQListString(context.faqList, context.faqId)
              if (faqList) extraData.faq_list = faqList
            }
          } else {
            // Standard field generation: use current form values and pending suggestions
            extraData.accepted_data = acceptedDataValue || undefined
            extraData.proposed_data = proposedDataValue || undefined
          }

          if (context?.user_instructions?.trim()) {
            extraData.user_instructions = context.user_instructions.trim()
          }

          // Merge any additional params from config (e.g., user_instructions)
          if (config.additionalParams) {
            Object.keys(config.additionalParams).forEach(key => {
              // Skip dynamic params that are built above
              if (key !== 'faq_list' && typeof config.additionalParams![key] === 'string') {
                extraData[key] = config.additionalParams![key] as string
              }
            })
          }

          if (formField === 'outline' && formType === 'cm') {
            const deliveryStyle = currentProps.moduleForm.getValues().deliveryStyle
            if (deliveryStyle?.includes('RELATED FILE')) {
              extraData.is_media_mode = 'true'
            }
          }

          const response = await api.safeRequest(api.generate, {
            ...idParams,
            system_prompt_template_id: config.systemPromptTemplateId,
            message_template_id: formType === 'cm' ? 'generate_cm_message' : 'generate_step_message',
            extra_data: extraData,
          })

          if (abortController.signal.aborted) return

          if (response.ok && response.data) {
            const suggestion = normalizeAISuggestion(response.data as string)
            updateFieldState(formField, { state: 'pending', suggestion }, formType)
          } else if (!response.ok && response.isNetworkError) {
            const entityType: PollEntityType = formType === 'cm' ? 'cm' : 'step'
            const entityId = formType === 'cm' ? currentProps.taskId : currentProps.stepId
            if (!entityId) {
              logAIGenerationError('Missing entity ID for poll fallback', null, formField)
              updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
              return
            }
            const pollResult = await pollForGenerationResult(
              api,
              { entityType, entityId, systemPromptTemplateId: config.systemPromptTemplateId },
              { signal: abortController.signal },
            )
            if (pollResult.status === 'done') {
              const suggestion = normalizeAISuggestion(pollResult.result)
              updateFieldState(formField, { state: 'pending', suggestion }, formType)
            } else if (pollResult.status === 'aborted') {
              // Cancelled by user — state already reset by cancelGeneration
            } else {
              const reason = pollResult.status === 'failed' ? pollResult.error : 'Polling timed out'
              logAIGenerationError(reason, null, formField)
              updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
            }
          } else {
            logAIGenerationError('Generate API response invalid or empty', response, formField)
            updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
          }
        } else if (config.type === 'specialized') {
          let response: any

          // Handle different endpoint parameter requirements
          if (
            config.endpoint === 'generateDemographic' ||
            config.endpoint === 'generateObjectives' ||
            config.endpoint === 'generateOutline'
          ) {
            // These endpoints only work with content modules (task_id)
            if (formType !== 'cm') {
              throw new Error(`${config.endpoint} only supports content modules`)
            }

            const form = getForm(currentProps, formType) as AICompatibleForm | undefined
            const currentValues = form?.getValues?.()
            const acceptedDataRaw = currentValues ? (currentValues[formField] as unknown) : undefined
            const acceptedDataValue = typeof acceptedDataRaw === 'string' ? acceptedDataRaw : undefined

            // Get current field state to check for pending suggestions
            const currentFieldState = getFieldState(formField, formType)
            const proposedDataValue = currentFieldState.state === 'pending' ? currentFieldState.suggestion : undefined

            response = await api.safeRequest(api[config.endpoint], {
              task_id: currentProps.taskId!,
              extra_data: {
                field_name: config.formField,
                accepted_data: acceptedDataValue || undefined,
                proposed_data: proposedDataValue || undefined,
              },
            })
          } else if (config.endpoint === 'generateFAQs') {
            // FAQs can work with both content modules and steps, but not resources
            if (formType === 'resource') {
              throw new Error('FAQ generation is not supported for resources')
            }
            const faqParams = formType === 'cm' ? { task_id: currentProps.taskId! } : { step_id: currentProps.stepId! }
            response = await api.safeRequest(api[config.endpoint], faqParams)
          } else if (config.endpoint === 'generateVideoMetadata' || config.endpoint === 'generateImageMetadata') {
            // Video and image metadata generation work with resources (resource_id)
            if (formType !== 'resource') {
              throw new Error(`${config.endpoint} only supports resources`)
            }
            if (!currentProps.resourceId) {
              throw new Error(`${config.endpoint} requires a resource_id`)
            }

            const form = getForm(currentProps, formType) as AICompatibleForm | undefined
            const currentValues = form?.getValues?.()

            // Get current title and description values to send as optional context
            const currentTitle = currentValues?.title as string | undefined
            const currentDescription = currentValues?.description as string | undefined

            // Prepare params based on endpoint type
            const params: any = { resource_id: currentProps.resourceId }

            if (config.endpoint === 'generateVideoMetadata') {
              if (currentTitle) params.video_title = currentTitle
              if (currentDescription) params.video_description = currentDescription
            } else if (config.endpoint === 'generateImageMetadata') {
              if (currentTitle) params.image_title = currentTitle
              if (currentDescription) params.image_description = currentDescription
            }

            response = await api.safeRequest(api[config.endpoint], params)
          }

          if (abortController.signal.aborted) return

          if (response.ok && response.data?.[config.responseField]) {
            let suggestion: string

            // Handle FAQs which return an array that needs to be serialized
            if (config.responseField === 'questions') {
              suggestion = JSON.stringify(response.data[config.responseField])
            } else {
              suggestion = response.data[config.responseField] as string
            }

            updateFieldState(formField, { state: 'pending', suggestion }, formType)
          } else {
            logAIGenerationError(`${config.endpoint} API response invalid or empty`, response, formField)
            updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
          }
        }
      } catch (error) {
        logAIGenerationError('Field generation request failed', error, formField)
        updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
      } finally {
        activeRequestsRef.current.delete(stateKey)
        abortControllersRef.current.delete(stateKey)
      }
    },
    [api, getFieldState, updateFieldState],
  )

  const acceptSuggestion = useCallback(
    (formField: string, type: FormType, updateServerFn?: (field: string) => void) => {
      const fieldState = getFieldState(formField, type)
      if (fieldState.state !== 'pending' || !fieldState.suggestion) return

      try {
        const form = getForm(propsRef.current, type)
        if (!form) {
          logAIGenerationError('Form not available', null, formField)
          return
        }

        const currentValues = form.getValues()
        if (!(formField in currentValues)) {
          logAIGenerationError('Form field not found', null, formField)
          return
        }

        // Type-safe form field setter
        setFormFieldValue(form as AICompatibleForm, formField, fieldState.suggestion)
        updateServerFn?.(formField)
        updateFieldState(formField, { state: 'idle', suggestion: null }, type)
      } catch (error) {
        logAIGenerationError('Failed to accept suggestion', error, formField)
        updateFieldState(formField, { state: 'idle', suggestion: null }, type)
      }
    },
    [getFieldState, updateFieldState],
  )

  const rejectSuggestion = useCallback(
    (formField: string, formType?: FormType) => {
      const fieldState = getFieldState(formField, formType)

      // Restore the pre-suggestion value so the DOM input reverts correctly.
      if (fieldState.previousValue != null) {
        const currentProps = propsRef.current
        const form = getForm(currentProps, formType ?? 'cm') as AICompatibleForm | undefined
        if (form) {
          form.setFieldValue(formField, fieldState.previousValue)
        }
      }

      updateFieldState(formField, { state: 'idle', suggestion: null, previousValue: null }, formType)
    },
    [getFieldState, updateFieldState],
  )

  const cancelGeneration = useCallback(
    (formField: string, formType?: FormType) => {
      const key = getStateKey(formField, formType)
      const controller = abortControllersRef.current.get(key)
      if (controller) {
        controller.abort()
        abortControllersRef.current.delete(key)
      }
      activeRequestsRef.current.delete(key)
      updateFieldState(formField, { state: 'idle', suggestion: null }, formType)
    },
    [updateFieldState],
  )

  const generateFieldByName = useCallback(
    (type: FormType, fieldName: string, context?: AIGenerationContext) => {
      const config = getGenerateFieldOptions(type, fieldName)
      if (config) {
        generateFieldUnified(config, context)
      }
    },
    [generateFieldUnified],
  )

  const regenerateFieldByName = useCallback(
    (type: FormType, fieldName: string, context?: AIGenerationContext) => {
      const config = getGenerateFieldOptions(type, fieldName)
      if (config) {
        generateFieldUnified(config, context)
      }
    },
    [generateFieldUnified],
  )

  // Generate field with a custom config (useful for dynamic configs)
  const generateFieldWithConfig = useCallback(
    (type: FormType, config: AIFieldConfig, context?: AIGenerationContext) => {
      generateFieldUnified({ ...config, formType: type }, context)
    },
    [generateFieldUnified],
  )

  // Cleanup function for form resets
  const resetAllFields = useCallback(() => {
    setFieldStates({})
  }, [])

  // Convenience accept functions
  const acceptCMSuggestion = useCallback(
    (formField: string, updateServerFn?: (field: string) => void) => {
      acceptSuggestion(formField, 'cm', updateServerFn)
    },
    [acceptSuggestion],
  )

  const acceptStepSuggestion = useCallback(
    (formField: string, updateServerFn?: (field: string) => void) => {
      acceptSuggestion(formField, 'step', updateServerFn)
    },
    [acceptSuggestion],
  )

  const acceptResourceSuggestion = useCallback(
    (formField: string, updateServerFn?: (field: string) => void) => {
      acceptSuggestion(formField, 'resource', updateServerFn)
    },
    [acceptSuggestion],
  )

  return {
    getFieldState,
    resetAllFields,

    acceptSuggestion,
    rejectSuggestion,
    cancelGeneration,

    generateFieldByName,
    regenerateFieldByName,
    generateFieldWithConfig,

    acceptCMSuggestion,
    acceptStepSuggestion,
    acceptResourceSuggestion,
  }
}
